import React, { useEffect, useState, useRef } from "react";
import GridViewIcon from "@mui/icons-material/GridView";
import ReorderIcon from "@mui/icons-material/Reorder";
import {
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tab,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Grid,
  Typography,
  Tooltip,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
} from "@mui/material";
import ArchiveIcon from "@mui/icons-material/Archive";
import StarIcon from "@mui/icons-material/Star";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDispatch, useSelector } from "react-redux";
import { backend_url } from "../../App";
import axios from "axios";
import {
  getEvents,
  selectEvents,
  resetEvents,
  updateEventStatus,
  deleteEvent,
  annotateEvents,
  removeAnnotations,
  setEvents,
} from "./eventsSlice";
import { selectFilters, setFilterApplied } from "../filters/filterSlice";
import { selectOrganization } from "../organization/organizationSlice";
import { Filters } from "../filters/Filters";

export function EventsTable() {
  const [state, setState] = useState({ page: 0, rowsPerPage: 10 });
  const [selected, setSelected] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const [listView, setListView] = useState(true);
  const [reload, setReload] = useState(false);
  const [tab, setTab] = useState(0);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showAnnotateMenu, setShowAnnotateMenu] = useState(false);
  const [selectedAnnotations, setSelectedAnnotations] = useState([]);
  const { species: allSpecies } = useSelector(selectOrganization);
  const prevTab = useRef(tab);
  const isFirstRender = useRef(true);
  const justRan = useRef(false);
  const { results: events, count } = useSelector(selectEvents);
  const filters = useSelector(selectFilters);
  const dispatch = useDispatch();
  const isFirstRun = useRef(true);
  const [countR, setCount] = useState(0);
  const [requests, setRequests] = useState([]);
  const [requestsPage, setRequestsPage] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const initialLoadDone = useRef(false);

  const status = (tab) => {
    switch (tab) {
      case 0:
        return "";
      case 1:
        return "ARCHIVED";
      case 2:
        return "FEATURED";
      case 3:
        return "REQUESTS";
      default:
        return "NONE";
    }
  };

  const { page, rowsPerPage } = state;

  // Initial load - fetch events on mount
  useEffect(() => {
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      const statusValue = tab === 3 ? status(0) : status(tab);
      dispatch(getEvents(1, filters.filterApplied, statusValue, rowsPerPage));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle page/rows per page changes
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    setSelected([]);
    dispatch(resetEvents());
  }, [rowsPerPage, dispatch]);

  // Handle filter changes
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (justRan.current) {
      justRan.current = false;
      return;
    }
    if (tab === 3) {
      dispatch(
        getEvents(
          state.page + 1,
          filters.filterApplied,
          status(0),
          rowsPerPage,
        ),
      );
    } else {
      dispatch(
        getEvents(
          state.page + 1,
          filters.filterApplied,
          status(tab),
          rowsPerPage,
        ),
      );
    }
    justRan.current = true;
    let check = filters.filterApplied ? false : filters.filterApplied;
    dispatch(setFilterApplied(check));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, rowsPerPage, state.page, tab]);

  useEffect(() => {
    if (prevTab !== tab && reload === true) {
      prevTab.current = tab;
    } else {
      setReload(true);
    }
  }, [tab, reload]);

  useEffect(() => {
    if (selectMode === false) {
      setSelected([]);
    }
  }, [selectMode]);

  // Handle tab changes - fetch new data
  useEffect(() => {
    if (initialLoadDone.current) {
      const statusValue = tab === 3 ? status(0) : status(tab);
      dispatch(resetEvents());
      setState({ page: 0, rowsPerPage: rowsPerPage });
      dispatch(getEvents(1, filters.filterApplied, statusValue, rowsPerPage));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const showChanges = async (tabChange = false) => {
    if (!tabChange) {
      if (events.length <= rowsPerPage) {
        dispatch(resetEvents());
      } else {
        dispatch(
          setEvents({
            results: events.slice(0, rowsPerPage * page),
            count: count,
            filterApplied: true,
          }),
        );
      }
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
    if (tab === 3) {
      dispatch(
        getEvents(
          state.page + 1,
          filters.filterApplied,
          status(0),
          rowsPerPage,
        ),
      );
    } else {
      dispatch(
        getEvents(
          state.page + 1,
          filters.filterApplied,
          status(tab),
          rowsPerPage,
        ),
      );
    }
  };

  const handleChangePage = (event, newPage) => {
    if (tab !== 3) {
      if (newPage > state.page && newPage + 1 > events.length / rowsPerPage)
        dispatch(
          getEvents(
            newPage + 1,
            filters.filterApplied,
            status(tab),
            rowsPerPage,
          ),
        );
      setState({ page: newPage, rowsPerPage: state.rowsPerPage });
    } else {
      if (
        newPage > requestsPage &&
        newPage + 1 > requests.length / rowsPerPage
      ) {
        fetchRequest(newPage + 1);
      }
      setRequestsPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setState({ page: 0, rowsPerPage: parseInt(event.target.value, 10) });
  };

  const handleArchive = () => {
    if (tab !== 1) {
      dispatch(updateEventStatus(selected, "archive"));
    } else {
      dispatch(updateEventStatus(selected, "restore"));
    }
    setSelected([]);
    if (tab === 2 || tab === 1) {
      showChanges();
    }
  };

  const handleStar = () => {
    if (tab !== 2) {
      dispatch(updateEventStatus(selected, "feature"));
    } else {
      dispatch(updateEventStatus(selected, "restore"));
    }
    setSelected([]);
    if (tab === 2 || tab === 1) {
      showChanges();
    }
  };

  const handleDelete = () => {
    dispatch(deleteEvent(selected));
    setShowDeleteConfirmation(false);
    setSelected([]);
    showChanges();
  };

  const handleAnnotate = () => {
    dispatch(annotateEvents(selected, selectedAnnotations));
    setShowAnnotateMenu(false);
    setSelected([]);
    showChanges();
  };

  const handleUnAnnotate = () => {
    dispatch(removeAnnotations(selected, selectedAnnotations));
    setShowAnnotateMenu(false);
    setSelected([]);
    showChanges();
  };

  const modifyRequest = async (eventId, status) => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        throw new Error("User is not authenticated");
      }

      const config = {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      };

      const requestData = {
        id: eventId,
        request_status: status,
        request_type: "DELETE",
      };

      const response = await axios.put(
        `${backend_url}/core/api/request/`,
        requestData,
        config,
      );

      console.log("Request updated:", response.data);
      setRequests([]);
      return response.data;
    } catch (error) {
      console.error("Error updating request:", error);
      throw error;
    }
  };

  const fetchRequest = async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      throw new Error("User is not authenticated");
    }

    const config = {
      headers: {
        Authorization: `Token ${token}`,
      },
    };

    try {
      let response = await axios.get(
        `${backend_url}/core/api/request/?page=${requestsPage + 1}&page_size=${rowsPerPage}`,
        config,
      );
      console.log(response.data);
      setRequests(response.data.results);
      setCount(response.data.count);
    } catch (error) {
      console.error("Error fetching requests:", error);
    }
  };

  // Format date and time for list view
  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");
      return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
      return dateString;
    }
  };

  // Format date for display in both list and grid views
  const formatDisplayDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const changeUrl = (img) => {
    return img;
  };

  const changeName = (name) => {
    let nameC;
    if (name.includes("PTZ")) {
      nameC = name.split("-");
      name = nameC[1];
    }
    return name;
  };

  const gradientStyle = {
    background:
      "linear-gradient(135deg, #454545 0%, rgba(129, 129, 129, 0.95) 100%)",
    color: "white",
  };

  return (
    <Paper sx={{ p: 0, m: 0, overflow: "hidden", borderRadius: 2 }}>
      <Dialog
        open={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Delete Event?</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete {selected.length} selected event(s)?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirmation(false)}>
            Cancel
          </Button>
          <Button onClick={handleDelete} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showAnnotateMenu}
        onClose={() => setShowAnnotateMenu(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Annotate Event(s)</DialogTitle>
        <DialogContent dividers>
          <DialogContentText id="alert-dialog-description">
            Select all the labels you want to apply (or remove)!
          </DialogContentText>
          <FormGroup>
            {allSpecies.map((species) => (
              <FormControlLabel
                control={<Checkbox name={species.name} />}
                label={species.name}
                key={species.name}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedAnnotations([
                      ...selectedAnnotations,
                      species.name,
                    ]);
                  } else {
                    setSelectedAnnotations(
                      selectedAnnotations.filter(
                        (item) => item !== species.name,
                      ),
                    );
                  }
                }}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowAnnotateMenu(false);
              setSelectedAnnotations([]);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleUnAnnotate}>UnAnnotate</Button>
          <Button onClick={handleAnnotate} autoFocus>
            Annotate
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          position: "sticky",
          top: 0,
          background: gradientStyle.background,
          zIndex: 2,
          px: 0,
        }}
      >
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          aria-label="basic tabs example"
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : false}
          allowScrollButtonsMobile={isMobile}
          sx={{
            "& .MuiTab-root": {
              fontSize: isMobile ? "0.7rem" : "0.875rem",
              minWidth: isMobile ? "auto" : 90,
              px: isMobile ? 1 : 2,
              color: "white",
              "&.Mui-selected": {
                color: "white",
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "white",
            },
          }}
        >
          <Tab label="All" />
          <Tab label="Archived" />
          <Tab label="Featured" />
          <Tab label="Requests" />
        </Tabs>

        <Box
          sx={{
            position: "absolute",
            right: 15,
            top: 8,
            display: "flex",
            alignItems: "center",
            gap: isMobile ? 0.5 : 1,
            flexDirection: isMobile ? "row" : "row",
            flexWrap: isMobile ? "wrap" : "nowrap",
            justifyContent: isMobile ? "flex-end" : "flex-start",
          }}
        >
          <Filters />

          {tab !== 3 && (
            <Button
              onClick={() => setSelectMode(!selectMode)}
              variant="text"
              size="small"
              sx={{
                color: "white",
                minWidth: isMobile ? "auto" : "auto",
                px: isMobile ? 1 : 2,
                py: isMobile ? 0.5 : 0.5,
                borderRadius: "30px",
                fontSize: isMobile ? "0.7rem" : "0.875rem",
                fontWeight: 500,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.2)",
                  transform: "translateY(-2px)",
                  transition: "all 0.3s ease",
                },
              }}
            >
              Select
            </Button>
          )}

          {listView ? (
            <Tooltip title="Grid View">
              <GridViewIcon
                sx={{
                  color: "white",
                  cursor: "pointer",
                  fontSize: isMobile ? 20 : 24,
                  "&:hover": { opacity: 0.7 },
                }}
                onClick={() => setListView(!listView)}
              />
            </Tooltip>
          ) : (
            <Tooltip title="List view">
              <ReorderIcon
                sx={{
                  color: "white",
                  cursor: "pointer",
                  fontSize: isMobile ? 20 : 24,
                  "&:hover": { opacity: 0.7 },
                }}
                onClick={() => setListView(!listView)}
              />
            </Tooltip>
          )}
        </Box>

        {selected.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: isMobile ? 1 : 2,
              py: isMobile ? 1 : 1,
              bgcolor: "rgba(255,255,255,0.95)",
              borderTop: "1px solid #e0e0e0",
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? 1 : 0,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {selected.length} selected
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: isMobile ? 1 : 2,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Button
                size="small"
                onClick={() => {
                  setShowAnnotateMenu(true);
                  setSelectedAnnotations([]);
                }}
                sx={{ fontSize: isMobile ? "0.7rem" : "0.875rem" }}
              >
                Annotations Menu
              </Button>
              <Box sx={{ display: "flex", gap: 1 }}>
                <div onClick={handleArchive} style={{ cursor: "pointer" }}>
                  {tab === 1 ? (
                    <UnarchiveIcon
                      sx={{ color: "red", fontSize: isMobile ? 20 : 24 }}
                    />
                  ) : (
                    <ArchiveIcon sx={{ fontSize: isMobile ? 20 : 24 }} />
                  )}
                </div>
                <StarIcon
                  onClick={handleStar}
                  sx={{
                    color: tab === 2 ? "red" : "black",
                    cursor: "pointer",
                    fontSize: isMobile ? 20 : 24,
                  }}
                />
                <DeleteIcon
                  onClick={() => setShowDeleteConfirmation(true)}
                  sx={{
                    color: "red",
                    cursor: "pointer",
                    fontSize: isMobile ? 20 : 24,
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          p: isMobile ? (listView ? 0 : 1) : listView ? 2 : 2,
          minHeight: 400,
        }}
      >
        {listView ? (
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: "none",
              border: "1px solid #e0e0e0",
              overflowX: "auto",
              borderRadius: 0,
            }}
          >
            <Table size="small" stickyHeader aria-label="sticky table">
              <TableHead>
                {tab === 3 ? (
                  <TableRow>
                    <TableCell>Event</TableCell>
                    <TableCell>Specie</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell>Updated At</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                ) : (
                  <TableRow>
                    {selectMode && (
                      <TableCell padding="checkbox" sx={{ width: 40 }}>
                        <Checkbox
                          checked={
                            events.length > 0 &&
                            events
                              .slice(
                                page * rowsPerPage,
                                page * rowsPerPage + rowsPerPage,
                              )
                              .every((event) => selected.includes(event.uuid))
                          }
                          onChange={() => {
                            const currentPageEvents = events.slice(
                              page * rowsPerPage,
                              page * rowsPerPage + rowsPerPage,
                            );
                            const allSelected = currentPageEvents.every(
                              (event) => selected.includes(event.uuid),
                            );
                            if (allSelected) {
                              setSelected(
                                selected.filter(
                                  (id) =>
                                    !currentPageEvents.some(
                                      (e) => e.uuid === id,
                                    ),
                                ),
                              );
                            } else {
                              setSelected([
                                ...selected,
                                ...currentPageEvents
                                  .map((e) => e.uuid)
                                  .filter((id) => !selected.includes(id)),
                              ]);
                            }
                          }}
                        />
                      </TableCell>
                    )}
                    <TableCell>Event</TableCell>
                    <TableCell>Species</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell>Confidence</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Camera</TableCell>
                  </TableRow>
                )}
              </TableHead>
              {tab === 3 ? (
                <TableBody>
                  {requests.length !== 0 ? (
                    requests.map((row) => {
                      return (
                        <TableRow key={row.id}>
                          <TableCell>
                            <a
                              target="_blank"
                              rel="noopener noreferrer"
                              href={row.thumbnail}
                            >
                              <img src={row.thumbnail} height={80} alt="" />
                            </a>
                          </TableCell>
                          <TableCell>
                            {row.species.map((item) => (
                              <Chip
                                className="mr1"
                                style={{ backgroundColor: item.color }}
                                color="primary"
                                key={item.key}
                                label={item.name}
                              />
                            ))}
                          </TableCell>
                          <TableCell>{row.request_status}</TableCell>
                          <TableCell>{row.request_type}</TableCell>
                          <TableCell>
                            {formatDateTime(row.created_at)}
                          </TableCell>
                          <TableCell>
                            {formatDateTime(row.updated_at)}
                          </TableCell>
                          {row.request_status === "PENDING" ? (
                            <TableCell>
                              <Button
                                onClick={() =>
                                  modifyRequest(row.id, "APPROVED")
                                }
                              >
                                APPROVE
                              </Button>
                              <Button
                                onClick={() =>
                                  modifyRequest(row.id, "REJECTED")
                                }
                              >
                                REJECT
                              </Button>
                            </TableCell>
                          ) : (
                            <TableCell>
                              <Button disabled>APPROVE</Button>
                              <Button disabled>REJECT</Button>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No requests found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              ) : (
                <TableBody>
                  {events.length !== 0 ? (
                    (rowsPerPage > 0
                      ? events.slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage,
                        )
                      : events
                    ).map((row) => {
                      return (
                        <TableRow key={row.uuid}>
                          {selectMode && (
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selected.includes(row.uuid)}
                                onChange={() => {
                                  if (selected.includes(row.uuid)) {
                                    setSelected(
                                      selected.filter((id) => id !== row.uuid),
                                    );
                                  } else {
                                    setSelected([...selected, row.uuid]);
                                  }
                                }}
                              />
                            </TableCell>
                          )}
                          <TableCell>
                            <a
                              target="_blank"
                              rel="noopener noreferrer"
                              href={changeUrl(row.file)}
                            >
                              <img
                                src={changeUrl(row.thumbnail)}
                                height={80}
                                alt=""
                                style={{ borderRadius: 4 }}
                              />
                            </a>
                          </TableCell>
                          <TableCell>
                            {row.species.map((item) => (
                              <Chip
                                className="mr1"
                                style={{ backgroundColor: item.color }}
                                color="primary"
                                key={item.key}
                                label={item.name}
                              />
                            ))}
                          </TableCell>
                          <TableCell>
                            {formatDateTime(row.created_at)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`${(row.confidence * 100).toFixed(1)}%`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{formatDisplayDate(row.date)}</TableCell>
                          <TableCell>{changeName(row.camera_name)}</TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={selectMode ? 8 : 7}
                        align="center"
                        sx={{ py: 4 }}
                      >
                        <Typography color="text.secondary">
                          No events found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              )}
            </Table>
          </TableContainer>
        ) : (
          <Box>
            {selectMode && (
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Checkbox
                  checked={
                    events.length > 0 &&
                    events
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      .every((event) => selected.includes(event.uuid))
                  }
                  onChange={() => {
                    const currentPageEvents = events.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage,
                    );
                    const allSelected = currentPageEvents.every((event) =>
                      selected.includes(event.uuid),
                    );
                    if (allSelected) {
                      setSelected(
                        selected.filter(
                          (id) => !currentPageEvents.some((e) => e.uuid === id),
                        ),
                      );
                    } else {
                      setSelected([
                        ...selected,
                        ...currentPageEvents
                          .map((e) => e.uuid)
                          .filter((id) => !selected.includes(id)),
                      ]);
                    }
                  }}
                />
                <Typography variant="body2" fontWeight={500}>
                  Select All
                </Typography>
              </Box>
            )}
            <Grid container spacing={2} sx={{ justifyContent: "center" }}>
              {events
                .slice(
                  state.page * state.rowsPerPage,
                  state.page * state.rowsPerPage + state.rowsPerPage,
                )
                .map((event) => {
                  return (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      xl={2.4}
                      key={event.uuid}
                      sx={{ display: "flex", justifyContent: "center" }}
                    >
                      <Card
                        sx={{
                          width: "100%",
                          maxWidth: 260,
                          borderRadius: 3,
                          boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
                          transition:
                            "transform 0.3s ease, box-shadow 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: "0 8px 25px rgba(0,0,0,0.12)",
                          },
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        {selectMode && (
                          <Checkbox
                            sx={{
                              position: "absolute",
                              top: 4,
                              left: 4,
                              zIndex: 1,
                              backgroundColor: "rgba(255,255,255,0.8)",
                              borderRadius: 1,
                            }}
                            checked={selected.includes(event.uuid)}
                            onChange={() => {
                              if (selected.includes(event.uuid)) {
                                setSelected(
                                  selected.filter(
                                    (item) => item !== event.uuid,
                                  ),
                                );
                              } else {
                                setSelected([...selected, event.uuid]);
                              }
                            }}
                          />
                        )}
                        <Box
                          sx={{
                            position: "relative",
                            pt: "75%",
                            overflow: "hidden",
                            bgcolor: "#f5f5f5",
                          }}
                        >
                          <img
                            src={changeUrl(event.thumbnail)}
                            alt=""
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: 0,
                              left: 0,
                              right: 0,
                              p: 1,
                              background:
                                "linear-gradient(transparent, rgba(0,0,0,0.6))",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "white", fontWeight: 500 }}
                            >
                              {changeName(event.camera_name)}
                            </Typography>
                          </Box>
                        </Box>
                        <CardContent sx={{ p: 1.5 }}>
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.5,
                              mb: 1,
                            }}
                          >
                            {event.species
                              .filter((item) => item.id !== "N/A")
                              .slice(0, 3)
                              .map((item) => (
                                <Chip
                                  key={item.id}
                                  label={item.name}
                                  size="small"
                                  sx={{
                                    backgroundColor: item.color,
                                    color: "white",
                                    fontSize: "0.65rem",
                                    height: 20,
                                  }}
                                />
                              ))}
                            {event.species.filter((item) => item.id !== "N/A")
                              .length > 3 && (
                              <Chip
                                label={`+${
                                  event.species.filter(
                                    (item) => item.id !== "N/A",
                                  ).length - 3
                                }`}
                                size="small"
                                sx={{ fontSize: "0.65rem", height: 20 }}
                              />
                            )}
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Confidence:
                            </Typography>
                            <Typography variant="caption" fontWeight={500}>
                              {(event.confidence * 100).toFixed(1)}%
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Date:
                            </Typography>
                            <Typography variant="caption">
                              {formatDisplayDate(event.date)}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
            </Grid>
            {events.length === 0 && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography color="text.secondary">No events found</Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      <TablePagination
        rowsPerPageOptions={[10, 20, 50, 100]}
        component="div"
        count={
          tab === 3
            ? countR !== null
              ? countR
              : 0
            : count !== null
              ? count
              : 0
        }
        rowsPerPage={rowsPerPage}
        page={tab === 3 ? requestsPage : page}
        onRowsPerPageChange={handleChangeRowsPerPage}
        onPageChange={handleChangePage}
        showFirstButton
        showLastButton
        sx={{
          "& .MuiTablePagination-toolbar": {
            flexWrap: isMobile ? "wrap" : "nowrap",
            gap: isMobile ? 1 : 0,
            minHeight: isMobile ? "auto" : "auto",
            padding: isMobile ? "8px 16px" : "0 16px",
          },
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select":
            {
              fontSize: isMobile ? "0.75rem" : "0.875rem",
            },
        }}
      />
    </Paper>
  );
}
